import React from 'react';
import { ToastContext, Form, Button, Icon, InputLabel, Input, SelectLabel, SelectHierarchyLabel, OptionalSection } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';
import moment from 'moment-timezone';
import LoadingCard from '../../../../../../components/loadingCard';
import css from './modal-popup.module.css';

function SelectedSeatsModal({
    form,
    setForm,
    organization,
    zone,
    chartId,
    categories,
    contingents,
    inventories,
    inventoriesOptions,
    sync,
    synczones,
    setFormObject,
    inputError,
    inputValidity = () => {}
}) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);

    const selectedSeatsCount = React.useMemo(() => {
        return form.selectedSeats.length;
    }, [form]);

    const categoriesOptions = React.useMemo(() => {
        let categoriesList = [
            { text: t('category.none'), value: 'none' },
            { text: t('category.keep_applied_category'), value: 'default' }
        ];
        categories.forEach(cat => {
            categoriesList.push({ text: cat.name, value: cat.uuid });
        });
        return categoriesList;
    }, [categories]);

    const contingentsOptions = React.useMemo(() => {
        let contingentsList = [
            { text: t('contingent.none'), value: 'none' },
            { text: t('contingent.keep_applied_contingent'), value: 'default' }
        ];
        contingents.forEach(cat => {
            contingentsList.push({ text: cat.name, value: cat.uuid });
        });
        return contingentsList;
    }, [contingents]);

    const propagationOptions = React.useMemo(() => {
        return [
            { text: t('ticket.charts.zone.propagate.options.all'), value: 0 },
            { text: t('ticket.charts.zone.propagate.options.active'), value: 1 },
            { text: t('ticket.charts.zone.propagate.options.manual'), value: 2 }
        ];
    });

    const manualSelection = React.useMemo(() => {
        return form.propagationMode === 2;
    }, [form, form.propagationMode]);

    const inventoriesUuids = React.useMemo(() => {
        return inventories.map(i => {
            return i.uuid;
        });
    }, [inventories]);

    const activeInventories = React.useMemo(() => {
        return inventories
            .filter(iv => iv.active && (iv.end_date === null || iv.end_date < moment().toISOString()))
            .map(i => {
                return i.uuid;
            });
    }, [inventories]);

    const payload = React.useMemo(() => {
        const list = [];
        form.selectedSeats.forEach(s => {
            let seat = {
                uuid: s.uuid,
                access: form.access || s.access,
                row: form.row || s.row,
                label: form.label || s.label,
                seat: form.seat || s.seat,
                propagate_to_inventories: inventoriesUuids
            };

            if (form.selectedContingent !== 'default') {
                seat['contingent'] = form.selectedContingent === 'none' ? null : form.selectedContingent;
            }
            if (form.selectedCategory !== 'default') {
                seat['category'] = form.selectedCategory === 'none' ? null : form.selectedCategory;
            }
            switch (form.propagationMode) {
                case 0:
                    break;
                case 1:
                    seat['propagate_to_inventories'] = activeInventories;
                    break;
                case 2:
                    seat['propagate_to_inventories'] = form.selectedInventories;
                    break;
                default:
                    break;
            }
            list.push(seat);
        });
        return list;
    }, [form, inventoriesUuids, activeInventories]);

    const disableSubmit = React.useMemo(() => {
        const inputs = [form.access, form.row, form.seat];

        return !inputs.every(inputValidity) || submitting;
    }, [form, submitting, inputValidity]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        fetcher
            .patch(`/ticket/organizations/${organization.id}/charts/${chartId}/zones/${zone.uuid}/seats`, { data: payload })
            .then(() => {
                toast.success(t('ticket.charts.zone.edition_success'));
                setForm('selectedSeats', []);
                sync();
                synczones();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.charts.newZone.failure'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [chartId, organization, zone, payload, sync, synczones]);

    return (
        <div className={css['modal-popup-container']}>
            <div className={css['modal-popup']}>
                <div className={css['modal-popup-header']}>
                    <div className={css['modal-popup-header-title']}>{`${selectedSeatsCount} ${t('seats_selected', { count: selectedSeatsCount })}`}</div>
                    <Icon
                        className={css['modal-popup-header-toggle']}
                        name="cross"
                        onClick={() =>
                            setFormObject({
                                ...form,
                                selectedSeats: [],
                                access: null,
                                row: null,
                                label: null,
                                seat: null,
                                selectedContingent: 'default',
                                selectedCategory: 'default'
                            })
                        }
                        size="regular"
                    />
                </div>

                <div className={css['modal-popup-body']}>
                    <LoadingCard loading={submitting}>
                        <SeatTitle form={form} categories={categories} />
                        <Form.Field>
                            <InputLabel label={t('ticket.charts.seats.access')} />
                            <Input
                                errors={Boolean(!inputValidity(form.access))}
                                onChange={({ target: { value } }) => setForm('access', value.trim().slice(0, 20))}
                                value={form.access || form.selectedSeats[0]?.access}
                            />
                            <Form.FieldErrors errors={[inputError(form.access)]} />
                        </Form.Field>
                        {selectedSeatsCount === 1 && (
                            <>
                                <Form.Field>
                                    <InputLabel label={t('ticket.charts.seats.row')} />
                                    <Input
                                        errors={Boolean(!inputValidity(form.row))}
                                        onChange={({ target: { value } }) => setForm('row', value.slice(0, 20))}
                                        value={form.row || form.selectedSeats[0]?.row}
                                    />
                                    <Form.FieldErrors errors={[inputError(form.row)]} />
                                </Form.Field>

                                <Form.Field>
                                    <InputLabel label={t('ticket.charts.seats.seat')} />
                                    <Input
                                        errors={Boolean(!inputValidity(form.seat))}
                                        onChange={({ target: { value } }) => setForm('seat', value.slice(0, 20))}
                                        value={form.seat || form.selectedSeats[0]?.seat}
                                    />
                                    <Form.FieldErrors errors={[inputError(form.seat)]} />
                                </Form.Field>
                            </>
                        )}

                        <div className={css['modal-popup-select']}>
                            <SelectLabel
                                label={t('ticket.common.seating.category')}
                                value={form.selectedCategory}
                                options={categoriesOptions}
                                onChange={value => {
                                    setForm('selectedCategory', value);
                                }}
                                useNew
                            />
                        </div>
                        <div className={css['modal-popup-select']}>
                            <SelectLabel
                                label={t('ticket.common.seating.contingent')}
                                value={form.selectedContingent}
                                options={contingentsOptions}
                                onChange={value => {
                                    setForm('selectedContingent', value);
                                }}
                                useNew
                            />
                        </div>

                        <OptionalSection label={t('ticket.charts.zone.propagate.label')}>
                            <SelectLabel
                                label={t('ticket.common.inventories')}
                                value={form.propagationMode}
                                options={propagationOptions}
                                onChange={value => setForm('propagationMode', value)}
                                disabled={!inventories.length}
                                useNew
                            />
                        </OptionalSection>
                        {manualSelection && (
                            <SelectHierarchyLabel
                                multi
                                height="auto"
                                searchable={false}
                                placeholder={t('ticket.common.action.select')}
                                options={inventoriesOptions.filter(i => i.value !== 'default')}
                                value={form.selectedInventories.length ? t('ticket.charts.chart.inventories_selected', { count: form.selectedInventories.length }) : null}
                                selected={form.selectedInventories}
                                label={t('ticket.common.inventories')}
                                onChange={selected => setForm('selectedInventories', selected)}
                            />
                        )}
                    </LoadingCard>
                </div>

                <div className={css['modal-popup-footer']}>
                    <Button fluid primary onClick={handleSubmit} label={t(`ticket.common.action.save`)} disabled={disableSubmit} />
                </div>
            </div>
        </div>
    );
}

export default SelectedSeatsModal;

export function SeatTitle({ form, categories }) {
    const seat = form.selectedSeats[0];
    const { t } = useTranslation();

    const category = React.useMemo(() => {
        let cat = categories.find(cat => seat.category === cat.uuid);
        if (!cat) {
            return '';
        }
        return cat.name;
    }, [seat, categories]);

    if (!form.selectedSeats.length || form.selectedSeats.length > 1) {
        return null;
    }
    return (
        <span className={css['selected-seats-title']}>
            <span>
                <Icon name="seat" />
            </span>
            <span>{`${t('ticket.common.seating.category')} ${category}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_seat')} ${seat.seat}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_row')} ${seat.row}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_access')} ${seat.access}`}</span>
        </span>
    );
}
